import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import PasswordInput from "@/components/PasswordInput";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, CheckCircle2 } from "lucide-react";

const MAX_FILE_MB = 3;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function RegisterClubPage() {
  const nav = useNavigate();
  const [districts, setDistricts] = useState([]);
  const [form, setForm] = useState({
    name: "", district: "", contact_person: "", email: "", phone: "", address: "", password: "", confirm: "",
  });
  const [logo, setLogo] = useState(null);
  const [courtPhotos, setCourtPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
  }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onLogoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`Logo must be under ${MAX_FILE_MB}MB`); return; }
    setLogo({ name: f.name, dataUrl: await fileToDataUrl(f) });
  };
  const onCourtsUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const added = [];
    for (const f of files) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`${f.name} exceeds ${MAX_FILE_MB}MB`); continue; }
      added.push({ name: f.name, dataUrl: await fileToDataUrl(f) });
    }
    setCourtPhotos((prev) => [...prev, ...added].slice(0, 6));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (!form.district) { toast.error("Please select a district"); return; }

    setSubmitting(true);
    try {
      await api.post("/clubs/register", {
        name: form.name,
        district: form.district,
        contact_person: form.contact_person,
        email: form.email,
        phone: form.phone,
        address: form.address,
        password: form.password,
        logo_data_url: logo?.dataUrl || null,
        court_photos_data_urls: courtPhotos.map((c) => c.dataUrl),
      });
      setDone(true);
      toast.success("Application submitted. Awaiting JPA approval.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="register-club-success" className="max-w-2xl mx-auto px-4 md:px-8 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-jpa-primary mx-auto mb-6" />
        <h1 className="font-display text-4xl tracking-wider mb-3">APPLICATION SUBMITTED</h1>
        <p className="text-jpa-muted mb-8">
          Thanks for registering <b>{form.name}</b>. Your application is now pending JPA approval.
          Once approved, you&apos;ll be able to log in with the email and password you just set and start creating tournaments.
        </p>
        <Button data-testid="go-home-btn" onClick={() => nav("/")} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
          Back to Home
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="register-club-page">
      <PageHero eyebrow="Club onboarding" title="REGISTER YOUR CLUB" subtitle="Get JPA-affiliated and start hosting sanctioned tournaments in Jharkhand."
        image="https://images.unsplash.com/photo-1617144520113-88ae706a86eb?w=1800" />

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-14">
        <form onSubmit={submit} className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Club name *</Label>
              <Input data-testid="club-name-input" value={form.name} onChange={setField("name")} required className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label>District *</Label>
              <Select value={form.district} onValueChange={setField("district")}>
                <SelectTrigger data-testid="club-district-select" className="mt-1 rounded-sm"><SelectValue placeholder="Select district" /></SelectTrigger>
                <SelectContent>
                  {districts.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contact person *</Label>
              <Input data-testid="club-contact-input" value={form.contact_person} onChange={setField("contact_person")} required className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label>Phone * (10 digits)</Label>
              <Input data-testid="club-phone-input" inputMode="numeric" maxLength={10} pattern="\d{10}" placeholder="9876543210" value={form.phone} onChange={(e) => setField("phone")({ target: { value: e.target.value.replace(/\D/g, "").slice(0, 10) } })} required className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input data-testid="club-email-input" type="email" value={form.email} onChange={setField("email")} required className="mt-1 rounded-sm" />
            </div>
            <div className="md:col-span-2">
              <Label>Address *</Label>
              <Textarea data-testid="club-address-input" value={form.address} onChange={setField("address")} required rows={2} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label>Password *</Label>
              <PasswordInput data-testid="club-password-input" toggleTestId="club-password-toggle" value={form.password} onChange={setField("password")} required className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label>Confirm password *</Label>
              <PasswordInput data-testid="club-confirm-input" toggleTestId="club-confirm-toggle" value={form.confirm} onChange={setField("confirm")} required className="mt-1 rounded-sm" />
            </div>
          </div>

          {/* Logo upload */}
          <div>
            <Label>Club logo (PNG/JPG, up to {MAX_FILE_MB}MB)</Label>
            <label htmlFor="logo-upload" className="mt-1 flex items-center gap-3 border-2 border-dashed border-jpa-border rounded-sm p-4 cursor-pointer hover:border-jpa-primary transition-colors">
              <UploadCloud className="w-6 h-6 text-jpa-primary" />
              <span className="text-sm text-jpa-muted">{logo ? logo.name : "Click to upload logo"}</span>
              <input id="logo-upload" data-testid="club-logo-upload" type="file" accept="image/*" className="hidden" onChange={onLogoUpload} />
            </label>
            {logo && (
              <div className="mt-3 relative w-24 h-24 border border-jpa-border">
                <img src={logo.dataUrl} alt="logo" className="w-full h-full object-cover" />
                <button type="button" onClick={() => setLogo(null)} className="absolute -top-2 -right-2 bg-jpa-live text-white rounded-full p-1"><X className="w-3 h-3" /></button>
              </div>
            )}
          </div>

          {/* Court photos */}
          <div>
            <Label>Court photos (up to 6, each under {MAX_FILE_MB}MB)</Label>
            <label htmlFor="courts-upload" className="mt-1 flex items-center gap-3 border-2 border-dashed border-jpa-border rounded-sm p-4 cursor-pointer hover:border-jpa-primary transition-colors">
              <UploadCloud className="w-6 h-6 text-jpa-primary" />
              <span className="text-sm text-jpa-muted">{courtPhotos.length > 0 ? `${courtPhotos.length} photo(s) added — click to add more` : "Click to upload court photos"}</span>
              <input id="courts-upload" data-testid="club-courts-upload" type="file" accept="image/*" multiple className="hidden" onChange={onCourtsUpload} />
            </label>
            {courtPhotos.length > 0 && (
              <div className="mt-3 grid grid-cols-3 md:grid-cols-6 gap-2">
                {courtPhotos.map((c, i) => (
                  <div key={i} className="relative aspect-square border border-jpa-border">
                    <img src={c.dataUrl} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => setCourtPhotos((p) => p.filter((_, j) => j !== i))} className="absolute -top-2 -right-2 bg-jpa-live text-white rounded-full p-1"><X className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-4 border-t border-jpa-border">
            <div className="text-xs text-jpa-muted">
              By registering, you agree to abide by the JPA sanctioning rules & the AIPA ranking policy.
              After JPA approval, log in with the email + password above at <Link to="/login" className="text-jpa-primary font-bold underline">/login</Link>.
            </div>
            <Button data-testid="club-submit-btn" type="submit" disabled={submitting} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm px-8 py-6">
              {submitting ? "Submitting…" : "Submit application"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
